import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServiceCard = ({ service, isExpanded, onToggle }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'capabilities', label: 'Capabilities', icon: 'Settings' },
    { id: 'timeline', label: 'Timeline', icon: 'Clock' },
    { id: 'outcomes', label: 'Outcomes', icon: 'Target' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground">{service?.description}</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-surface rounded-lg p-4">
                <div className="text-lg font-semibold text-primary mb-1">
                  €{service?.startingPrice?.toLocaleString()}
                </div>
                <div className="text-sm text-muted-foreground">Starting from</div>
              </div>
              <div className="bg-surface rounded-lg p-4">
                <div className="text-lg font-semibold text-primary mb-1">
                  {service?.duration}
                </div>
                <div className="text-sm text-muted-foreground">Typical duration</div>
              </div>
            </div>
          </div>
        );
      case 'capabilities':
        return (
          <div className="space-y-3">
            {service?.capabilities?.map((capability, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="p-1 bg-secondary/10 rounded">
                  <Icon name="Check" size={16} className="text-secondary" />
                </div>
                <div>
                  <div className="font-medium text-primary">{capability?.title}</div>
                  <div className="text-sm text-muted-foreground">{capability?.description}</div>
                </div>
              </div>
            ))}
          </div>
        );
      case 'timeline':
        return (
          <div className="space-y-4">
            {service?.timeline?.map((phase, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center text-white text-sm font-semibold">
                    {index + 1}
                  </div>
                  {index < service?.timeline?.length - 1 && (
                    <div className="w-0.5 h-8 bg-border mt-2"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-primary">{phase?.phase}</div>
                  <div className="text-sm text-muted-foreground mb-2">{phase?.description}</div>
                  <div className="text-xs text-secondary font-medium">{phase?.duration}</div>
                </div>
              </div>
            ))}
          </div>
        );
      case 'outcomes':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {service?.outcomes?.map((outcome, index) => (
                <div key={index} className="bg-surface rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name={outcome?.icon} size={20} className="text-secondary" />
                    <div className="font-medium text-primary">{outcome?.metric}</div>
                  </div>
                  <div className="text-2xl font-bold text-secondary mb-1">{outcome?.value}</div>
                  <div className="text-sm text-muted-foreground">{outcome?.description}</div>
                </div>
              ))}
            </div>
            {service?.caseStudy && (
              <div className="bg-primary/5 rounded-lg p-4 border border-primary/10">
                <div className="flex items-start space-x-3">
                  <Icon name="Quote" size={20} className="text-primary mt-1" />
                  <div>
                    <p className="text-primary italic mb-2">"{service?.caseStudy?.quote}"</p>
                    <div className="text-sm text-muted-foreground">
                      — {service?.caseStudy?.client}, {service?.caseStudy?.company}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-elevation border border-border overflow-hidden hover:shadow-2xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300 ease-out">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-secondary/10 rounded-lg hover:bg-secondary/20 hover:scale-110 transition-all duration-300">
              <Icon name={service?.icon} size={24} className="text-secondary" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-primary mb-2">{service?.title}</h3>
              <p className="text-muted-foreground">{service?.subtitle}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            onClick={onToggle}
            className="hover:scale-110 active:scale-95 transition-transform duration-200"
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {service?.tags?.map((tag, index) => (
            <span
              key={index}
              className="px-3 py-1 bg-surface text-primary text-sm rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>

        {!isExpanded && (
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <span>From €{service?.startingPrice?.toLocaleString()}</span>
              <span>•</span>
              <span>{service?.duration}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="ArrowRight"
              iconPosition="right"
              onClick={onToggle}
              className="hover:scale-105 active:scale-95 transition-transform duration-200"
            >
              Learn More
            </Button>
          </div>
        )}
      </div>
      {isExpanded && (
        <div className="border-t border-border">
          <div className="flex border-b border-border">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 text-sm font-medium transition-all duration-200 hover:scale-105 active:scale-95 ${
                  activeTab === tab?.id
                    ? 'text-secondary border-b-2 border-secondary bg-secondary/5' :'text-muted-foreground hover:text-primary hover:bg-surface'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span className="hidden sm:inline">{tab?.label}</span>
              </button>
            ))}
          </div>
          <div className="p-6">
            {renderTabContent()}
            <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-6 border-t border-border">
              <Button
                variant="default"
                iconName="Calendar"
                iconPosition="left"
                className="bg-secondary hover:bg-secondary/90 hover:scale-105 active:scale-95 transition-all duration-200"
                onClick={() => window.location.href = '/contact-multi-channel-engagement-hub'}
              >
                Schedule Consultation
              </Button>
              <Button
                variant="outline"
                iconName="Calculator"
                iconPosition="left"
                onClick={() => window.location.href = '/roi-calculator-suite-value-demonstration-tools'}
                className="hover:scale-105 active:scale-95 transition-all duration-200"
              >
                Calculate ROI
              </Button>
              <Button
                variant="ghost"
                iconName="FileText"
                iconPosition="left"
                onClick={() => window.location.href = '/data-maturity-calculator-lead-generation-assessment'}
                className="hover:scale-105 active:scale-95 transition-all duration-200"
              >
                Assessment Tool
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceCard;